/**
 * The farm page's Schedules tab: the schedule builder and rain skip for one
 * site at a time, picked from the farm's sites (?site=), opening on a program
 * when a link names one (?program=). It used to be a tab of the controller
 * page, three taps from the farm; a grower thinks in farms, and the
 * controller page is now the installer's.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import EspNowScheduleBuilder from './EspNowScheduleBuilder';
import RainSkipPanel from './RainSkipPanel';
import espnowService, {
  type EspNowController,
  type EspNowRouter,
  type MasterPump,
  type ScheduleProgram,
} from '../../services/espnowIrrigation';
import type { OverviewController } from '../../services/irrigationOverview';

interface SiteData {
  controller: EspNowController;
  routers: EspNowRouter[];
  pumps: MasterPump[];
  programs: ScheduleProgram[];
}

export default function FarmSchedulesTab({
  controllers,
  orgTimezone,
}: {
  controllers: OverviewController[];
  orgTimezone: string;
}) {
  const [params, setParams] = useSearchParams();
  const wanted = params.get('site');
  const siteId = controllers.some((c) => c.controller_id === wanted)
    ? wanted!
    : controllers[0]?.controller_id ?? null;
  const focusProgramId = params.get('program');

  const [data, setData] = useState<SiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) { setLoading(false); return; }
    let active = true;
    setLoading(true);
    setError(null);
    setData(null);
    Promise.all([
      espnowService.getControllers(),
      espnowService.getRouters(siteId).catch(() => [] as EspNowRouter[]),
      espnowService.getMasterPumps(siteId).catch(() => [] as MasterPump[]),
      espnowService.getPrograms(siteId).catch(() => [] as ScheduleProgram[]),
    ])
      .then(([all, routers, pumps, programs]) => {
        if (!active) return;
        const controller = all.find((c) => c.controller_id === siteId);
        if (!controller) { setError('That site could not be found.'); return; }
        setData({ controller, routers, pumps, programs });
      })
      .catch(() => { if (active) setError('Could not load the schedules.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [siteId]);

  const pickSite = (id: string) => {
    const p = new URLSearchParams(params);
    p.set('site', id);
    p.delete('program');
    setParams(p, { replace: true });
  };

  if (controllers.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
        No irrigation controller is on this farm yet, so there is nothing to schedule.
      </div>
    );
  }

  return (
    <div className="min-h-96">
      {controllers.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Site">
          {controllers.map((c) => (
            <button
              key={c.controller_id}
              type="button"
              onClick={() => pickSite(c.controller_id)}
              aria-pressed={c.controller_id === siteId}
              className={`rounded-full px-3 py-1 text-sm font-semibold ${
                c.controller_id === siteId ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-100'
              }`}
            >
              {c.controller_name || c.controller_id}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center text-gray-400">
          <RefreshCw className="mr-2 h-5 w-5 animate-spin" /> Loading schedules…
        </div>
      ) : error || !data ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error ?? 'Could not load the schedules.'}
        </div>
      ) : (
        <>
          <RainSkipPanel
            controller={data.controller}
            programs={data.programs}
            routers={data.routers}
            orgTimezone={orgTimezone}
          />
          <EspNowScheduleBuilder
            key={data.controller.controller_id}
            controller={data.controller}
            initialProgramId={focusProgramId}
            programs={data.programs}
            routers={data.routers}
            masterPumps={data.pumps}
            orgTimezone={orgTimezone}
            onProgramsChange={(programs) => setData((d) => (d ? { ...d, programs } : d))}
          />
        </>
      )}
    </div>
  );
}
